import { memo } from "react";
import type { ReactNode } from "react";

import { useNavigate } from "react-router-dom";
import { ContentCard } from "@narmi/design_system";

interface OptionCardProps {
  title: string | ReactNode;
  icon?: string;
  iconSize?: string;
  description?: string;
  customOptionButtons?: ReactNode;
  shouldOpenInNewTab?: boolean;
  /* React router route to follow when user clicks on the card */
  route?: string;
  /* Card click callback */
  onCardClick?: () => void;
}

function OptionCard({
  icon,
  title,
  description,
  iconSize,
  route,
  onCardClick,
  customOptionButtons,
  shouldOpenInNewTab = false,
}: OptionCardProps) {
  const navigate = useNavigate();

  const handleClick = (clickedRoute?: string) => {
    // override default click behavior if a custom handler is passed
    if (onCardClick) {
      onCardClick();
      return;
    }

    if (!clickedRoute) {
      return;
    }

    if (!shouldOpenInNewTab) {
      navigate(clickedRoute); // if route is undefined, the page won't change
    } else {
      const url = clickedRoute.startsWith("/")
        ? clickedRoute
        : `${window.location.pathname.replace(/\/$/, "")}${clickedRoute}`;
      window.open(url, "_blank", "noreferrer");
    }
  };

  return (
    <ContentCard
      kind="button"
      paddingSize="l"
      onClick={() => handleClick(route)}
    >
      <h4
        className="fontFamily--body"
        style={{ color: "var(--theme-primary)" }}
      >
        {title}
        {icon && (
          <i
            className={`narmi-icon-${icon} padding--left--xs`}
            style={{
              fontSize: iconSize,
              transform: "translateY(10%)",
              display: "inline-block",
            }}
          />
        )}
      </h4>
      <p className="fontColor--primary margin--none padding--top--xs">
        {description}
      </p>

      {customOptionButtons || null}
    </ContentCard>
  );
}

export default memo(OptionCard);
